import { useEffect, useRef, useState } from "react";

const CHECK_INTERVAL_MS = 60 * 1000;

const getLoadedMainJs = (): string | null => {
  const script = document.querySelector('script[src*="/static/js/main."]');
  return script ? script.getAttribute("src") : null;
};

const checkForNewVersion = async (): Promise<string | null> => {
  try {
    const response = await fetch("/asset-manifest.json", { cache: "no-store" });
    if (!response.ok) return null;
    const manifest = await response.json();
    return manifest?.files?.["main.js"] || null;
  } catch {
    // dev local sem build (npm start) ou rede indisponível - ignora silenciosamente
    return null;
  }
};

// Detecta quando o JS carregado nesta aba ficou desatualizado em relação ao
// que está publicado (ex: aba de clínica aberta por horas/dias, um deploy
// novo saiu nesse meio tempo). NÃO recarrega sozinho - já recarregou "na
// cara" do usuário no meio de um cadastro (paciente/cirurgia) e derrubou
// formulário em andamento, principalmente em janelas com deploys seguidos.
// Em vez disso só sinaliza (`updateAvailable`) pra UI oferecer o reload,
// e quem decide quando é o usuário (`applyUpdate`).
export const useVersionCheck = () => {
  const loadedMainJsRef = useRef<string | null>(null);
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    loadedMainJsRef.current = getLoadedMainJs();
    if (!loadedMainJsRef.current) return;

    const runCheck = async () => {
      const currentMainJs = await checkForNewVersion();
      if (!currentMainJs || !loadedMainJsRef.current) return;
      if (currentMainJs === loadedMainJsRef.current) return;
      setUpdateAvailable(true);
    };

    // Checa uma vez já no carregamento da página (cobre o caso comum de login
    // logo em seguida), além do intervalo de 60s e do gatilho de troca de
    // aba abaixo. MainLayout fica montado a sessão inteira (não remonta ao
    // navegar entre rotas), então sem um intervalo curto quem fica na mesma
    // aba sem trocar só via um deploy novo até 15min depois (ou nunca, se
    // não trocar de aba) — intervalo caiu de 15min pra 60s por isso.
    runCheck();

    const interval = setInterval(runCheck, CHECK_INTERVAL_MS);

    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      runCheck();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const applyUpdate = () => {
    localStorage.setItem(
      "app_updated_message",
      "O sistema foi atualizado para a versão mais recente."
    );
    window.location.reload();
  };

  return { updateAvailable, applyUpdate };
};
